/**
 * Queue API Layer - Schemas and Transformers
 */

import { z } from '@ufc/core'
import type {
  LoadingLocation,
  LoadingLocationFormData,
  LoadingLocationFilter,
  LoadingLocationRule,
  LoadingLocationRuleCity,
  LoadingLocationRuleFilter,
  LoadingLocationRuleFormData,
  QueueEntry,
  QueueEntryFilter,
  JoinQueueData,
  ValidateEntryData,
  ExitData,
} from '../domain/types'

// ============================================================================
// Loading Location Schemas
// ============================================================================

const loadingLocationVariantSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  name: z
    .union([
      z.string(),
      z.object({
        en: z.string().optional(),
        ar: z.string().optional(),
      }),
    ])
    .optional(),
  variantCode: z.string().optional(),
})

export const loadingLocationDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  tenant: z
    .object({
      id: z.union([z.string(), z.number()]),
      documentId: z.string(),
      name: z
        .union([
          z.string(),
          z.object({
            en: z.string().optional(),
            ar: z.string().optional(),
          }),
        ])
        .optional(),
    })
    .optional()
    .nullable(),
  factory: z
    .object({
      id: z.union([z.string(), z.number()]),
      documentId: z.string(),
      name: z
        .union([
          z.string(),
          z.object({
            en: z.string().optional(),
            ar: z.string().optional(),
          }),
        ])
        .optional(),
    })
    .optional()
    .nullable(),
  locationKind: z.enum(['bay', 'silo']),
  locationNumber: z.number(),
  label: z.string(),
  description: z.string().optional().nullable(),
  isActive: z.boolean(),
  variant: loadingLocationVariantSchema.optional().nullable(),
  allowedVariants: z.array(loadingLocationVariantSchema).optional().nullable(),
  windowNumber: z.number().optional().nullable(),
  monitorIndex: z.number().optional().nullable(),
  maxVehicles: z.number(),
  currentOccupancy: z.number().optional().nullable(),
  operatingHours: z.any().optional().nullable(),
  priority: z.number().optional().nullable(),
  averageLoadingTime: z.number().optional().nullable(),
  metadata: z.any().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type LoadingLocationDto = z.infer<typeof loadingLocationDtoSchema>

export const loadingLocationsResponseSchema = z.object({
  data: z.array(loadingLocationDtoSchema),
  meta: z
    .object({
      pagination: z
        .object({
          page: z.number(),
          pageSize: z.number(),
          pageCount: z.number(),
          total: z.number(),
        })
        .optional(),
    })
    .optional(),
})

export type LoadingLocationsResponse = z.infer<
  typeof loadingLocationsResponseSchema
>

export const createLoadingLocationRequestSchema = z.object({
  locationKind: z.enum(['bay', 'silo']),
  locationNumber: z.number().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
  variant: z.union([z.string(), z.number()]).optional(),
  allowedVariants: z.array(z.union([z.string(), z.number()])).optional(),
  maxVehicles: z.number().min(1).default(1),
  priority: z.number().optional(),
  averageLoadingTime: z.number().optional(),
  factory: z.union([z.string(), z.number()]),
  tenant: z.union([z.string(), z.number()]).optional(),
})

export type CreateLoadingLocationRequest = z.infer<
  typeof createLoadingLocationRequestSchema
>

// ============================================================================
// Queue Entry Schemas
// ============================================================================

export const queueEntryDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  tenant: z
    .object({
      id: z.union([z.string(), z.number()]),
      documentId: z.string(),
      name: z.string().optional(),
    })
    .optional()
    .nullable(),
  order: z
    .object({
      id: z.union([z.string(), z.number()]).optional(),
      documentId: z.string(),
      orderNumber: z.string().optional(),
      orderStatus: z.string().optional(),
      variant: z
        .object({
          id: z.union([z.string(), z.number()]),
          documentId: z.string(),
          name: z.string().optional(),
          variantCode: z.string().optional(),
          packaging: z.enum(['bags', 'bulk']).optional(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  loadingLocation: z
    .object({
      id: z.union([z.string(), z.number()]),
      documentId: z.string(),
      label: z.string().optional(),
      locationKind: z.enum(['bay', 'silo']).optional(),
      locationNumber: z.number().optional(),
    })
    .optional()
    .nullable(),
  queueNumber: z.number(),
  displayNumber: z.string(),
  queueStatus: z.enum([
    'waiting',
    'ready',
    'called',
    'entered',
    'loading',
    'completed',
    'cancelled',
    'expired',
    'no_show',
  ]),
  entryQrCodeUrl: z.string().optional().nullable(),
  ticketPdfUrl: z.string().optional().nullable(),
  requestedAt: z.string(),
  notifiedAt: z.string().optional().nullable(),
  calledAt: z.string().optional().nullable(),
  enteredFactoryAt: z.string().optional().nullable(),
  arrivedLoadingAt: z.string().optional().nullable(),
  loadingStartedAt: z.string().optional().nullable(),
  loadingCompletedAt: z.string().optional().nullable(),
  exitedFactoryAt: z.string().optional().nullable(),
  expiresAt: z.string().optional().nullable(),
  driverPhone: z.string().optional().nullable(),
  driverName: z.string().optional().nullable(),
  vehiclePlate: z.string().optional().nullable(),
  vehicleType: z.string().optional().nullable(),
  estimatedLoadingTime: z.number().optional().nullable(),
  actualLoadingTime: z.number().optional().nullable(),
  entryGate: z
    .object({
      id: z.union([z.string(), z.number()]),
      name: z.string().optional(),
    })
    .optional()
    .nullable(),
  exitGate: z
    .object({
      id: z.union([z.string(), z.number()]),
      name: z.string().optional(),
    })
    .optional()
    .nullable(),
  cancellationReason: z.string().optional().nullable(),
  cancelledBy: z.string().optional().nullable(),
  cancelledAt: z.string().optional().nullable(),
  metadata: z.any().optional().nullable(),
  position: z.number().optional().nullable(),
  estimatedWaitTime: z.number().optional().nullable(),
  isEligibleToEnter: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type QueueEntryDto = z.infer<typeof queueEntryDtoSchema>

export const joinQueueRequestSchema = z.object({
  orderId: z.union([z.string(), z.number()]),
  cityId: z.union([z.string(), z.number()]).optional(),
  driverPhone: z.string().optional(),
  driverName: z.string().optional(),
  vehiclePlate: z.string().optional(),
  vehicleType: z.string().optional(),
})

export type JoinQueueRequest = z.infer<typeof joinQueueRequestSchema>

// ============================================================================
// Queue Event Schemas
// ============================================================================

export const queueEventDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  tenant: z
    .object({
      id: z.union([z.string(), z.number()]),
      documentId: z.string(),
      name: z.string().optional(),
    })
    .optional()
    .nullable(),
  queueEntry: z
    .object({
      id: z.union([z.string(), z.number()]),
      documentId: z.string(),
      displayNumber: z.string().optional(),
    })
    .optional()
    .nullable(),
  eventType: z.enum([
    'queue_joined',
    'status_changed',
    'notification_sent',
    'entry_validated',
    'entered_factory',
    'entered_loading',
    'exited_factory',
    'slot_released',
    'cancelled',
  ]),
  fromStatus: z.string().optional().nullable(),
  toStatus: z.string().optional().nullable(),
  triggeredBy: z.string().optional().nullable(),
  gate: z
    .object({
      id: z.union([z.string(), z.number()]),
      name: z.string().optional(),
    })
    .optional()
    .nullable(),
  notificationMethod: z.string().optional().nullable(),
  notificationPayload: z.any().optional().nullable(),
  validationResult: z.any().optional().nullable(),
  metadata: z.any().optional().nullable(),
  createdAt: z.string(),
})

export type QueueEventDto = z.infer<typeof queueEventDtoSchema>

function getDisplayName(
  value?: string | { en?: string; ar?: string } | null
): string {
  if (!value) return ''
  if (typeof value === 'string') return value

  return value.en ?? value.ar ?? ''
}

// ============================================================================
// Transformers
// ============================================================================

export function transformDtoToLoadingLocation(
  dto: LoadingLocationDto
): LoadingLocation {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    tenant: dto.tenant
      ? {
          id: String(dto.tenant.id),
          documentId: dto.tenant.documentId,
          name: getDisplayName(dto.tenant.name),
        }
      : undefined,
    factory: dto.factory
      ? {
          id: String(dto.factory.id),
          documentId: dto.factory.documentId,
          name: getDisplayName(dto.factory.name),
        }
      : undefined,
    locationKind: dto.locationKind,
    locationNumber: dto.locationNumber,
    label: dto.label,
    description: dto.description || undefined,
    isActive: dto.isActive,
    variant: dto.variant
      ? {
          id: String(dto.variant.id),
          documentId: dto.variant.documentId,
          name: getDisplayName(dto.variant.name),
          variantCode: dto.variant.variantCode || '',
        }
      : undefined,
    allowedVariants: dto.allowedVariants?.map((variant) => ({
      id: String(variant.id),
      documentId: variant.documentId,
      name: getDisplayName(variant.name),
      variantCode: variant.variantCode || '',
    })),
    maxVehicles: dto.maxVehicles,
    currentOccupancy: dto.currentOccupancy || 0,
    operatingHours: dto.operatingHours || undefined,
    priority: dto.priority || 0,
    averageLoadingTime: dto.averageLoadingTime || undefined,
    metadata: dto.metadata || undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function transformDtosToLoadingLocations(
  dtos: LoadingLocationDto[]
): LoadingLocation[] {
  return dtos.map(transformDtoToLoadingLocation)
}

export function transformDtoToQueueEntry(dto: QueueEntryDto): QueueEntry {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    tenant: dto.tenant
      ? {
          id: String(dto.tenant.id),
          documentId: dto.tenant.documentId,
          name: dto.tenant.name || '',
        }
      : undefined,
    order: dto.order
      ? {
          id: String(dto.order.id ?? dto.order.documentId),
          documentId: dto.order.documentId,
          orderNumber:
            dto.order.orderNumber ||
            (dto.metadata as Record<string, unknown> | null | undefined)
              ?.orderNumber as string | undefined,
          orderStatus: dto.order.orderStatus,
          variant: dto.order.variant
            ? {
                id: String(dto.order.variant.id),
                documentId: dto.order.variant.documentId,
                name: dto.order.variant.name || '',
                variantCode: dto.order.variant.variantCode || undefined,
                packaging: dto.order.variant.packaging || undefined,
              }
            : undefined,
        }
      : // order relation not populated (permissions) — build a stub from metadata
        (dto.metadata as Record<string, unknown> | null | undefined)?.orderNumber
        ? {
            id: '',
            documentId: '',
            orderNumber: (dto.metadata as Record<string, unknown>)
              .orderNumber as string,
          }
        : undefined,
    loadingLocation: dto.loadingLocation
      ? {
          id: String(dto.loadingLocation.id),
          documentId: dto.loadingLocation.documentId,
          label: dto.loadingLocation.label || '',
          locationKind: dto.loadingLocation.locationKind || 'bay',
          locationNumber: dto.loadingLocation.locationNumber || 0,
        }
      : undefined,
    queueNumber: dto.queueNumber,
    displayNumber: dto.displayNumber,
    queueStatus: dto.queueStatus,
    entryQrCodeUrl: dto.entryQrCodeUrl || undefined,
    ticketPdfUrl: dto.ticketPdfUrl || undefined,
    requestedAt: dto.requestedAt,
    notifiedAt: dto.notifiedAt || undefined,
    calledAt: dto.calledAt || undefined,
    enteredFactoryAt: dto.enteredFactoryAt || undefined,
    arrivedLoadingAt: dto.arrivedLoadingAt || undefined,
    loadingStartedAt: dto.loadingStartedAt || undefined,
    loadingCompletedAt: dto.loadingCompletedAt || undefined,
    exitedFactoryAt: dto.exitedFactoryAt || undefined,
    expiresAt: dto.expiresAt || undefined,
    driverPhone: dto.driverPhone || undefined,
    driverName: dto.driverName || undefined,
    vehiclePlate: dto.vehiclePlate || undefined,
    vehicleType: dto.vehicleType || undefined,
    estimatedLoadingTime: dto.estimatedLoadingTime || undefined,
    actualLoadingTime: dto.actualLoadingTime || undefined,
    entryGate: dto.entryGate
      ? { id: String(dto.entryGate.id), name: dto.entryGate.name || '' }
      : undefined,
    exitGate: dto.exitGate
      ? { id: String(dto.exitGate.id), name: dto.exitGate.name || '' }
      : undefined,
    cancellationReason: dto.cancellationReason || undefined,
    cancelledBy: dto.cancelledBy || undefined,
    cancelledAt: dto.cancelledAt || undefined,
    metadata: dto.metadata || undefined,
    position: dto.position || undefined,
    estimatedWaitTime: dto.estimatedWaitTime || undefined,
    isEligibleToEnter: dto.isEligibleToEnter,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function transformDtosToQueueEntries(
  dtos: QueueEntryDto[]
): QueueEntry[] {
  return dtos.map(transformDtoToQueueEntry)
}

export function transformToLocationRequest(
  data: Partial<LoadingLocationFormData>
): Partial<CreateLoadingLocationRequest> {
  const request: Partial<CreateLoadingLocationRequest> = {}

  if (data.locationKind !== undefined) request.locationKind = data.locationKind
  if (data.locationNumber !== undefined) request.locationNumber = data.locationNumber
  if (data.label !== undefined) request.label = data.label
  if (data.description !== undefined) request.description = data.description
  if (data.isActive !== undefined) request.isActive = data.isActive
  if (data.variantId !== undefined) request.variant = data.variantId
  if (data.allowedVariantIds !== undefined) request.allowedVariants = data.allowedVariantIds
  if (data.maxVehicles !== undefined) request.maxVehicles = data.maxVehicles
  if (data.priority !== undefined) request.priority = data.priority
  if (data.averageLoadingTime !== undefined) {
    request.averageLoadingTime = data.averageLoadingTime
  }
  if (data.factoryId !== undefined) request.factory = data.factoryId
  if (data.tenantId !== undefined) request.tenant = data.tenantId

  return request
}

export function transformToCreateLocationRequest(
  data: LoadingLocationFormData
): CreateLoadingLocationRequest {
  return transformToLocationRequest(data) as CreateLoadingLocationRequest
}

export function transformToJoinQueueRequest(
  data: JoinQueueData
): JoinQueueRequest {
  return {
    orderId: data.orderId,
    cityId: data.cityId,
    driverPhone: data.driverPhone,
    driverName: data.driverName,
    vehiclePlate: data.vehiclePlate,
    vehicleType: data.vehicleType,
  }
}

// ============================================================================
// Loading Location Rule Schemas
// ============================================================================

export const loadingLocationRuleCityDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  name: z.string(),
  locale: z.string().optional(),
  localizations: z.any().optional(),
})

export const loadingLocationRuleTenantDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  name: z.string().optional(),
})

export const loadingLocationRuleDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  loadingLocation: z.object({
    id: z.union([z.string(), z.number()]),
    documentId: z.string(),
    label: z.string(),
    locationKind: z.enum(['bay', 'silo']).optional(),
    locationNumber: z.number().optional(),
  }),
  cities: z.array(loadingLocationRuleCityDtoSchema).optional().nullable(),
  tenants: z.array(loadingLocationRuleTenantDtoSchema).optional().nullable(),
  priority: z.number().default(0),
  isActive: z.boolean().default(true),
  description: z.string().optional().nullable(),
  metadata: z.any().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type LoadingLocationRuleDto = z.infer<typeof loadingLocationRuleDtoSchema>

export const createLoadingLocationRuleRequestSchema = z.object({
  loadingLocation: z.union([z.string(), z.number()]),
  cities: z.array(z.union([z.string(), z.number()])).optional().nullable(),
  tenants: z.array(z.union([z.string(), z.number()])).optional().nullable(),
  priority: z.number().default(0),
  isActive: z.boolean().default(true),
  description: z.string().optional(),
})

export type CreateLoadingLocationRuleRequest = z.infer<
  typeof createLoadingLocationRuleRequestSchema
>

// ============================================================================
// Loading Location Rule Transformers
// ============================================================================

function extractCityWithNames(city: any): LoadingLocationRuleCity {
  let nameEn = city.name
  let nameAr: string | undefined

  const localizations = city.localizations?.data ?? city.localizations ?? []
  if (Array.isArray(localizations)) {
    for (const loc of localizations) {
      const attrs = loc?.attributes ?? loc
      if (attrs?.locale === 'en' && attrs?.name) nameEn = attrs.name
      if (attrs?.locale === 'ar' && attrs?.name) nameAr = attrs.name
    }
  }

  return {
    id: String(city.id),
    documentId: city.documentId,
    name: city.name,
    nameEn,
    nameAr,
  }
}

export function transformDtoToLoadingLocationRule(
  dto: LoadingLocationRuleDto
): LoadingLocationRule {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    loadingLocation: {
      id: String(dto.loadingLocation.id),
      documentId: dto.loadingLocation.documentId,
      label: dto.loadingLocation.label,
      locationKind: dto.loadingLocation.locationKind ?? 'bay',
      locationNumber: dto.loadingLocation.locationNumber ?? 0,
    },
    cities: dto.cities?.map(extractCityWithNames) ?? undefined,
    tenants: dto.tenants?.map((t) => ({
      id: String(t.id),
      documentId: t.documentId,
      name: t.name ?? '',
    })) ?? undefined,
    priority: dto.priority,
    isActive: dto.isActive,
    description: dto.description || undefined,
    metadata: dto.metadata || undefined,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function transformDtosToLoadingLocationRules(
  dtos: LoadingLocationRuleDto[]
): LoadingLocationRule[] {
  return dtos.map(transformDtoToLoadingLocationRule)
}

export function transformToLoadingLocationRuleRequest(
  data: LoadingLocationRuleFormData
): CreateLoadingLocationRuleRequest {
  return {
    loadingLocation: data.loadingLocationId,
    cities: data.cityIds,
    tenants: data.tenantIds,
    priority: data.priority,
    isActive: data.isActive,
    description: data.description,
  }
}

export function buildRuleQueryString(filter: LoadingLocationRuleFilter): string {
  const params = new URLSearchParams()

  if (filter.isActive && filter.isActive !== 'all') {
    params.append(
      'filters[isActive][$eq]',
      filter.isActive === 'active' ? 'true' : 'false'
    )
  }
  if (filter.loadingLocationId) {
    params.append(
      'filters[loadingLocation][documentId][$eq]',
      filter.loadingLocationId
    )
  }
  if (filter.cityId) {
    params.append('filters[cities][documentId][$eq]', filter.cityId)
  }

  params.append('populate[loadingLocation][fields][0]', 'documentId')
  params.append('populate[loadingLocation][fields][1]', 'label')
  params.append('populate[loadingLocation][fields][2]', 'locationKind')
  params.append('populate[loadingLocation][fields][3]', 'locationNumber')
  params.append('populate[cities][fields][0]', 'documentId')
  params.append('populate[cities][fields][1]', 'name')
  params.append('populate[cities][fields][2]', 'locale')
  params.append('populate[cities][populate][localizations][fields][0]', 'name')
  params.append('populate[cities][populate][localizations][fields][1]', 'locale')
  params.append('populate[tenants][fields][0]', 'documentId')
  params.append('populate[tenants][fields][1]', 'name')
  params.append('locale', '')

  return params.toString()
}

// ============================================================================
// Query Builders
// ============================================================================

export function buildLocationQueryString(
  filter: LoadingLocationFilter
): string {
  const params = new URLSearchParams()

  if (filter.locationKind && filter.locationKind !== 'all') {
    params.append('filters[locationKind][$eq]', filter.locationKind)
  }
  if (filter.isActive && filter.isActive !== 'all') {
    params.append(
      'filters[isActive][$eq]',
      filter.isActive === 'active' ? 'true' : 'false'
    )
  }
  if (filter.factoryId) {
    params.append('filters[factory][documentId][$eq]', filter.factoryId)
  }
  if (filter.variantId) {
    params.append('filters[$or][0][variant][documentId][$eq]', filter.variantId)
    params.append(
      'filters[$or][1][allowedVariants][documentId][$eq]',
      filter.variantId
    )
  }

  params.append('populate[tenant][fields][0]', 'documentId')
  params.append('populate[tenant][fields][1]', 'name')
  params.append('populate[factory][fields][0]', 'documentId')
  params.append('populate[factory][fields][1]', 'name')
  params.append('populate[variant][fields][0]', 'documentId')
  params.append('populate[variant][fields][1]', 'name')
  params.append('populate[variant][fields][2]', 'variantCode')
  params.append('populate[variant][fields][3]', 'packaging')
  params.append('populate[allowedVariants][fields][0]', 'documentId')
  params.append('populate[allowedVariants][fields][1]', 'name')
  params.append('populate[allowedVariants][fields][2]', 'variantCode')
  params.append('populate[allowedVariants][fields][3]', 'packaging')

  return params.toString()
}

export function buildEntryQueryString(filter: QueueEntryFilter): string {
  const params = new URLSearchParams()

  if (filter.status && filter.status !== 'all') {
    if (filter.status === 'active') {
      const activeStatuses = ['waiting', 'ready', 'called', 'entered', 'loading']
      activeStatuses.forEach((s, i) => {
        params.append(`filters[queueStatus][$in][${i}]`, s)
      })
    } else {
      params.append('filters[queueStatus][$eq]', filter.status)
    }
  }
  if (filter.loadingLocationId) {
    params.append(
      'filters[loadingLocation][documentId][$eq]',
      filter.loadingLocationId
    )
  }

  if (filter.startDate) {
    params.append('filters[requestedAt][$gte]', filter.startDate)
  }

  params.append('pagination[pageSize]', '100')

  // Always populate relations
  params.append('populate[tenant]', 'true')
  // Strapi v5: do NOT include the numeric 'id' in fields[] — it is not selectable
  // and silently breaks the entire fields list. documentId is always returned automatically.
  params.append('populate[order][fields][0]', 'documentId')
  params.append('populate[order][fields][1]', 'orderNumber')
  params.append('populate[order][fields][2]', 'orderStatus')
  params.append('populate[order][populate][variant][populate]', 'true')
  params.append('populate[loadingLocation]', 'true')
  params.append('populate[entryGate]', 'true')
  params.append('populate[exitGate]', 'true')

  return params.toString()
}

// ============================================================================
// Validators
// ============================================================================

export function validateLoadingLocationDto(
  data: unknown
): LoadingLocationDto | null {
  const result = loadingLocationDtoSchema.safeParse(data)
  return result.success ? result.data : null
}

export function validateQueueEntryDto(data: unknown): QueueEntryDto | null {
  const result = queueEntryDtoSchema.safeParse(data)
  return result.success ? result.data : null
}
